import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import 'material-symbols';
import { createTrackingValue } from '@/lib/stringUtils';
import { cn } from '@/lib/utils';

export interface NavMenuItemProps {
    title: string;
    iconName: string;
    path: string;
    /** Light-mode active fill, mirroring the section's hub icon color. Dark mode is uniform bluebell 500. */
    activeClassName: string;
    onNavigate: () => void;
}

/** The item minus its callback — the shape `NavMenuPanel` lists its entries in. */
export type NavMenuItemConfig = Omit<NavMenuItemProps, 'onNavigate'>;

export const NavMenuItem = ({ title, iconName, path, activeClassName, onNavigate }: NavMenuItemProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = useMemo(() => {
        return location.pathname === `/${path}` || location.pathname.startsWith(`/${path}/`);
    }, [location.pathname, path]);

    const onClick = useCallback(() => {
        navigate(path);
        onNavigate();
    }, [navigate, path, onNavigate]);

    return (
        <button
            type="button"
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
                'flex w-full items-center gap-3 rounded-full px-4 py-3 text-left transition-colors',
                'text-[18px] leading-6 font-medium light:text-gz-neutral-900 dark:text-gz-neutral-100',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary-accent/50',
                'light:focus-visible:ring-gz-marine-700/50 dark:focus-visible:ring-gz-marine-500/50',
                isActive
                    ? cn(activeClassName, 'dark:bg-gz-blue-500')
                    : 'light:hover:bg-gz-tan-350 dark:hover:bg-gz-blue-600',
            )}
            data-track-click="Navigation Menu Button Clicked"
            data-track-location="menu"
            data-track-button-name={createTrackingValue('nav', title)}
        >
            <span
                aria-hidden
                className={cn(
                    'material-symbols-rounded shrink-0 !text-2xl',
                    isActive && '[font-variation-settings:\'FILL\'_1]',
                )}
            >
                {iconName}
            </span>
            {title}
        </button>
    );
};
